import { createFileRoute } from '@tanstack/react-router';
import { Bell, FileWarning, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { CommandSelect } from '@/shared/ui/command-select';
import { ConfirmDialog } from '@/shared/ui/confirm-dialog';
import { EmptyState } from '@/shared/ui/empty-state';
import { Pagination } from '@/shared/ui/pagination';
import { SearchInput } from '@/shared/ui/search-input';
import {
  SearchableMultiSelect,
  SearchableSelect,
  type SearchableOption,
} from '@/shared/ui/searchable-select';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/shadcn/card';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { Separator } from '@/shared/ui/shadcn/separator';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';
import { ThemeToggle } from '@/shared/ui/theme';

/**
 * Página /styleguide — solo en dev. Muestra los tokens y componentes del
 * sistema de diseño. Sirve como verificación visual y QA accesible.
 */
export const Route = createFileRoute('/styleguide')({
  component: StyleguidePage,
});

function StyleguidePage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="mx-auto max-w-5xl space-y-10 p-6">
      <header className="flex items-center justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Sistema de diseño
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">Styleguide</h1>
        </div>
        <ThemeToggle />
      </header>

      <Section title="Tipografía">
        <div className="space-y-1">
          <p className="text-3xl font-semibold">Heading 3xl · Inter 600</p>
          <p className="text-2xl font-semibold">Heading 2xl · Inter 600</p>
          <p className="text-xl font-medium">Heading xl · Inter 500</p>
          <p className="text-lg">Subtítulo lg</p>
          <p className="text-base">Texto base 16px</p>
          <p className="text-sm text-muted-foreground">Texto secundario sm</p>
          <p className="font-mono text-xs">codigo-mono · INC-2026-0001</p>
        </div>
      </Section>

      <Section title="Color">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Swatch name="background" />
          <Swatch name="card" />
          <Swatch name="primary" />
          <Swatch name="muted" />
          <Swatch name="accent" />
          <Swatch name="destructive" />
          <Swatch name="border" />
          <Swatch name="ring" />
        </div>
      </Section>

      <Section title="Estados de incidencia">
        <div className="flex flex-wrap gap-2">
          <Badge>borrador</Badge>
          <Badge variant="info">notificado</Badge>
          <Badge variant="info">en atención</Badge>
          <Badge variant="warning">ampliación</Badge>
          <Badge variant="destructive">vencido</Badge>
          <Badge variant="success">cerrado</Badge>
          <Badge variant="outline">rechazado</Badge>
        </div>
      </Section>

      <Section title="Botones">
        <div className="flex flex-wrap gap-2">
          <Button>Default</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="link">Link</Button>
          <Button disabled>Disabled</Button>
          <Button size="sm">Small</Button>
          <Button size="lg">Large</Button>
        </div>
      </Section>

      <Section title="Inputs">
        <div className="grid max-w-md gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="email">Correo</Label>
            <Input id="email" type="email" placeholder="usuario@ani.gob.gt" />
          </div>
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar usuarios…" />
        </div>
      </Section>

      <Section title="Card + Skeleton">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Tarjeta de ejemplo</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Cards usan padding 24, radius 8 y borde 1px sólido. Sombra sutil.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-1/2" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        </div>
      </Section>

      <Section title="Tabs">
        <Tabs defaultValue="info">
          <TabsList>
            <TabsTrigger value="info">Información</TabsTrigger>
            <TabsTrigger value="hist">Historial</TabsTrigger>
            <TabsTrigger value="adj">Adjuntos</TabsTrigger>
          </TabsList>
          <TabsContent value="info">
            <p className="text-sm text-muted-foreground">Contenido de información.</p>
          </TabsContent>
          <TabsContent value="hist">
            <p className="text-sm text-muted-foreground">Contenido de historial.</p>
          </TabsContent>
          <TabsContent value="adj">
            <p className="text-sm text-muted-foreground">Contenido de adjuntos.</p>
          </TabsContent>
        </Tabs>
      </Section>

      <Section title="Empty state">
        <EmptyState
          icon={<FileWarning />}
          title="Sin incidencias activas"
          description="Cuando se notifique la primera incidencia aparecerá aquí."
          action={<Button size="sm">Crear incidencia</Button>}
        />
      </Section>

      <Section title="Toasts (sonner)">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => toast.success('Guardado')}>
            Success
          </Button>
          <Button variant="outline" onClick={() => toast.error('Algo falló')}>
            Error
          </Button>
          <Button variant="outline" onClick={() => toast('Notificación', { icon: <Bell /> })}>
            Default
          </Button>
        </div>
      </Section>

      <Section title="Confirm dialog (irreversible)">
        <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
          <Trash2 /> Eliminar registro
        </Button>
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Eliminar registro de prueba"
          description="Esta acción no se puede deshacer."
          confirmWord="ELIMINAR"
          confirmLabel="Eliminar"
          variant="destructive"
          onConfirm={() => {
            toast.success('Registro eliminado');
          }}
        />
      </Section>

      <Section title="Pagination">
        <Pagination page={page} pageSize={20} total={183} onPageChange={setPage} />
      </Section>

      <Section title="Searchable Select (react-select unstyled)">
        <SearchableSelectShowcase />
      </Section>

      <Section title="Command Select (búsqueda embebida — para Dialogs/Sheets)">
        <CommandSelectShowcase />
      </Section>
    </div>
  );
}

const SHOWCASE_OPTIONS: ReadonlyArray<SearchableOption<number>> = [
  { value: 1, label: 'Origen Estatal' },
  { value: 2, label: 'Origen Privado' },
  { value: 3, label: 'Asocio Público-Privado' },
  { value: 4, label: 'Cooperación Internacional' },
  { value: 5, label: 'Préstamo Multilateral' },
  { value: 6, label: 'Donación' },
];

const ESTADO_OPTIONS: ReadonlyArray<SearchableOption<string>> = [
  { value: 'PendienteInicio', label: 'Pendiente de inicio' },
  { value: 'Activo', label: 'Activo' },
  { value: 'Suspendido', label: 'Suspendido' },
  { value: 'Archivado', label: 'Archivado' },
];

function SearchableSelectShowcase() {
  const [singleEmpty, setSingleEmpty] = useState<number | null>(null);
  const [singleFilled, setSingleFilled] = useState<number | null>(2);
  const [singleError, setSingleError] = useState<number | null>(null);
  const [multi, setMulti] = useState<string[]>(['PendienteInicio', 'Activo']);

  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="sb-empty">Single — cerrado (placeholder)</Label>
        <SearchableSelect<number>
          id="sb-empty"
          value={singleEmpty}
          onChange={setSingleEmpty}
          options={SHOWCASE_OPTIONS}
          placeholder="Seleccione un origen…"
        />
        <p className="text-xs text-muted-foreground">
          Click o tab + Enter para abrir, tipear filtra el menú.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sb-filled">Single — con valor + clearable</Label>
        <SearchableSelect<number>
          id="sb-filled"
          value={singleFilled}
          onChange={setSingleFilled}
          options={SHOWCASE_OPTIONS}
          isClearable
        />
        <p className="text-xs text-muted-foreground">
          La X aparece solo cuando hay value y `isClearable` está activo.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sb-error">Single — estado error (ariaInvalid)</Label>
        <SearchableSelect<number>
          id="sb-error"
          value={singleError}
          onChange={setSingleError}
          options={SHOWCASE_OPTIONS}
          ariaInvalid
        />
        <p className="text-sm text-destructive">Este campo es obligatorio.</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sb-disabled">Single — disabled</Label>
        <SearchableSelect<number>
          id="sb-disabled"
          value={3}
          onChange={() => {}}
          options={SHOWCASE_OPTIONS}
          isDisabled
        />
        <p className="text-xs text-muted-foreground">No abre menú; cursor not-allowed.</p>
      </div>

      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="sb-multi">Multi — chips dentro del control</Label>
        <SearchableMultiSelect<string>
          id="sb-multi"
          value={multi}
          onChange={setMulti}
          options={ESTADO_OPTIONS}
          placeholder="Filtre por estado…"
        />
        <p className="text-xs text-muted-foreground">
          Cada chip removible por X. Menú queda abierto tras seleccionar (closeMenuOnSelect=false
          por default).
        </p>
      </div>
    </div>
  );
}

const PERMISO_OPTIONS: ReadonlyArray<SearchableOption<string>> = [
  { value: 'proyectos:crear', label: 'proyectos:crear', description: 'Crear nuevos proyectos' },
  {
    value: 'proyectos:listar',
    label: 'proyectos:listar',
    description: 'Ver el listado de proyectos',
  },
  {
    value: 'incidencias:reportar',
    label: 'incidencias:reportar',
    description: 'Reportar incidencias',
  },
  {
    value: 'incidencias:verificar',
    label: 'incidencias:verificar',
    description: 'Verificar incidencias resueltas',
  },
  {
    value: 'usuarios:administrar',
    label: 'usuarios:administrar',
    description: 'Administrar cuentas de usuario',
  },
];

function CommandSelectShowcase() {
  const [permiso, setPermiso] = useState<string | null>('proyectos:listar');

  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <div className="space-y-2">
        <Label asChild>
          <span>Con valor + clearable (click al seleccionado deselecciona)</span>
        </Label>
        <CommandSelect<string>
          label="Permiso"
          value={permiso}
          onChange={setPermiso}
          options={PERMISO_OPTIONS}
          placeholder="Busque un permiso…"
          emptyMessage="Ningún permiso coincide."
          isClearable
        />
        <p className="text-xs text-muted-foreground">
          Input + lista siempre visibles, sin menú flotante: no porta nada a `document.body`, por
          eso funciona dentro de Dialogs modales de Radix (donde `SearchableSelect` no).
        </p>
      </div>

      <div className="space-y-2">
        <Label asChild>
          <span>Disabled</span>
        </Label>
        <CommandSelect<string>
          label="Permiso deshabilitado"
          value={'proyectos:crear'}
          onChange={() => {}}
          options={PERMISO_OPTIONS}
          isDisabled
        />
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <Separator />
      {children}
    </section>
  );
}

function Swatch({ name }: { name: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div
        className="mb-2 h-12 rounded"
        style={{ backgroundColor: `var(--${name})` }}
        aria-hidden="true"
      />
      <p className="font-mono text-xs text-muted-foreground">--{name}</p>
    </div>
  );
}
